import { forwardRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { CheckCircle2, Send } from "lucide-react";
import { Reveal } from "./Reveal";

const BUDGETS = [
  "Below ₹10 Lakh",
  "₹10 – 20 Lakh",
  "₹20 – 35 Lakh",
  "₹35 Lakh & above",
];
const TIMES = ["Morning (9am–12pm)", "Afternoon (12pm–4pm)", "Evening (4pm–8pm)"];

export const LeadForm = forwardRef((props, ref) => {
  const [form, setForm] = useState({
    name: "",
    phone: "",
    budget: "",
    time: "",
  });
  const [errors, setErrors] = useState({});
  const [submitted, setSubmitted] = useState(false);

  const update = (key, val) => {
    setForm((f) => ({ ...f, [key]: val }));
    setErrors((e) => ({ ...e, [key]: undefined }));
  };

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = "Please enter your name.";
    if (!/^[6-9]\d{9}$/.test(form.phone.trim()))
      e.phone = "Enter a valid 10-digit mobile number.";
    if (!form.budget) e.budget = "Please select a budget range.";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = (ev) => {
    ev.preventDefault();
    if (validate()) setSubmitted(true);
  };

  const inputCls =
    "w-full rounded-xl border bg-white px-4 py-3.5 text-[#1F291E] placeholder:text-[#9AA396] outline-none transition-all focus:ring-2 focus:ring-[#2C5F2D]/40 focus:border-[#2C5F2D]";

  return (
    <section
      id="contact"
      ref={ref}
      className="py-20 md:py-28 px-4 sm:px-8 lg:px-16 bg-[#F4EFEA] scroll-mt-20"
    >
      <div className="max-w-3xl mx-auto">
        <Reveal>
          <div className="text-center mb-10">
            <p className="text-xs sm:text-sm uppercase tracking-[0.2em] font-semibold text-[#C15B3D] mb-4">
              Register Your Interest
            </p>
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-[#1F291E] leading-tight">
              Get a Call Back
            </h2>
            <p className="mt-5 text-lg text-[#5C6B5A]">
              Share your details and our team will reach out to schedule your
              private site visit.
            </p>
          </div>
        </Reveal>

        <Reveal delay={0.1}>
          <div
            data-testid="lead-capture-form-container"
            className="rounded-3xl bg-[#F7F3EE] border border-[#D5C8B8] p-7 sm:p-10 shadow-xl"
          >
            <AnimatePresence mode="wait">
              {submitted ? (
                <motion.div
                  key="thanks"
                  data-testid="lead-form-thank-you-card"
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                  className="text-center py-10"
                >
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ delay: 0.15, type: "spring", stiffness: 200 }}
                    className="mx-auto h-20 w-20 rounded-full bg-[#2C5F2D] grid place-items-center mb-6"
                  >
                    <CheckCircle2 className="h-11 w-11 text-white" strokeWidth={1.8} />
                  </motion.div>
                  <h3 className="font-serif text-3xl text-[#1F291E]">
                    Thank You, {form.name.split(" ")[0]}!
                  </h3>
                  <p className="mt-4 text-[#5C6B5A] text-lg max-w-md mx-auto leading-relaxed">
                    Your enquiry has been received. Our team will call you on{" "}
                    <span className="font-semibold text-[#2C5F2D]">
                      {form.phone}
                    </span>{" "}
                    shortly to arrange your visit to Aaroh Farms.
                  </p>
                  <button
                    onClick={() => {
                      setSubmitted(false);
                      setForm({ name: "", phone: "", budget: "", time: "" });
                    }}
                    className="mt-8 rounded-full border-2 border-[#2C5F2D] px-7 py-3 text-sm font-semibold text-[#2C5F2D] transition-all hover:bg-[#2C5F2D] hover:text-white"
                  >
                    Submit Another Enquiry
                  </button>
                </motion.div>
              ) : (
                <motion.form
                  key="form"
                  onSubmit={handleSubmit}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="space-y-5"
                  noValidate
                >
                  <div>
                    <label className="block text-sm font-medium text-[#1F291E] mb-2">
                      Full Name <span className="text-[#C15B3D]">*</span>
                    </label>
                    <input
                      data-testid="lead-form-name-input"
                      type="text"
                      value={form.name}
                      onChange={(e) => update("name", e.target.value)}
                      placeholder="Your full name"
                      className={`${inputCls} ${
                        errors.name ? "border-[#C15B3D]" : "border-[#D5C8B8]"
                      }`}
                    />
                    {errors.name && (
                      <p className="mt-1.5 text-sm text-[#C15B3D]">{errors.name}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-[#1F291E] mb-2">
                      Mobile Number (WhatsApp){" "}
                      <span className="text-[#C15B3D]">*</span>
                    </label>
                    <input
                      data-testid="lead-form-phone-input"
                      type="tel"
                      value={form.phone}
                      onChange={(e) =>
                        update("phone", e.target.value.replace(/\D/g, "").slice(0, 10))
                      }
                      placeholder="10-digit mobile number"
                      className={`${inputCls} ${
                        errors.phone ? "border-[#C15B3D]" : "border-[#D5C8B8]"
                      }`}
                    />
                    {errors.phone && (
                      <p className="mt-1.5 text-sm text-[#C15B3D]">{errors.phone}</p>
                    )}
                  </div>

                  <div className="grid sm:grid-cols-2 gap-5">
                    <div>
                      <label className="block text-sm font-medium text-[#1F291E] mb-2">
                        Budget <span className="text-[#C15B3D]">*</span>
                      </label>
                      <select
                        data-testid="lead-form-budget-select"
                        value={form.budget}
                        onChange={(e) => update("budget", e.target.value)}
                        className={`${inputCls} appearance-none ${
                          errors.budget ? "border-[#C15B3D]" : "border-[#D5C8B8]"
                        } ${!form.budget ? "text-[#9AA396]" : ""}`}
                      >
                        <option value="">Select budget</option>
                        {BUDGETS.map((b) => (
                          <option key={b} value={b} className="text-[#1F291E]">
                            {b}
                          </option>
                        ))}
                      </select>
                      {errors.budget && (
                        <p className="mt-1.5 text-sm text-[#C15B3D]">
                          {errors.budget}
                        </p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-[#1F291E] mb-2">
                        Preferred Time{" "}
                        <span className="text-[#9AA396] text-xs">(optional)</span>
                      </label>
                      <select
                        data-testid="lead-form-time-select"
                        value={form.time}
                        onChange={(e) => update("time", e.target.value)}
                        className={`${inputCls} appearance-none border-[#D5C8B8] ${
                          !form.time ? "text-[#9AA396]" : ""
                        }`}
                      >
                        <option value="">Select time slot</option>
                        {TIMES.map((t) => (
                          <option key={t} value={t} className="text-[#1F291E]">
                            {t}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <button
                    data-testid="lead-form-submit-button"
                    type="submit"
                    className="group w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#C15B3D] px-8 py-4 text-base font-semibold text-white shadow-lg shadow-[#C15B3D]/25 transition-all duration-300 hover:bg-[#A84C31] hover:-translate-y-0.5"
                  >
                    Submit Enquiry
                    <Send className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                  </button>
                  <p className="text-center text-xs text-[#5C6B5A]">
                    We respect your privacy. Your details are only used to
                    contact you about Aaroh Farms.
                  </p>
                </motion.form>
              )}
            </AnimatePresence>
          </div>
        </Reveal>
      </div>
    </section>
  );
});

LeadForm.displayName = "LeadForm";
